import { History, Heart, Sparkles } from 'lucide-react'
import { cn } from '@/shared/lib/utils'

export interface ProductCharacteristicsProps {
  /**
   * История чая
   */
  history?: any
  /**
   * Полезные свойства
   */
  benefits?: any
  /**
   * Состояние/эффект от чая
   */
  mood?: any
}

/**
 * Виджет: Характеристики чая (история и полезные свойства)
 * 
 * Отображает историю чая и полезные свойства, если они доступны.
 */
export function ProductCharacteristics({
  history,
  benefits,
  mood,
}: ProductCharacteristicsProps) {
  // Проверяем, есть ли данные для отображения
  const hasHistory = history && (
    (typeof history === 'string' && history.trim() !== '') ||
    (typeof history === 'object' && Object.keys(history).length > 0)
  )

  const hasBenefits = benefits && (
    (typeof benefits === 'string' && benefits.trim() !== '') ||
    (typeof benefits === 'object' && Object.keys(benefits).length > 0) ||
    (Array.isArray(benefits) && benefits.length > 0)
  )

  const hasMood = mood && (
    (typeof mood === 'string' && mood.trim() !== '') ||
    (typeof mood === 'object' && Object.keys(mood).length > 0)
  )

  if (!hasHistory && !hasBenefits && !hasMood) {
    return null
  }
  
  // Форматируем текст
  const formatText = (data: any): string | null => {
    if (!data) return null
    if (typeof data === 'string') return data.trim() || null
    if (typeof data === 'object') {
      // Если это объект с текстом
      if (data.text) return data.text
      if (data.description) return data.description
      if (data.content) return data.content
      // Если это массив
      if (Array.isArray(data)) {
        return data.map((item: any) => 
          typeof item === 'string' ? item : item.text || item.description || item.content || ''
        ).filter(Boolean).join('\n\n')
      }
      // Если это объект с ключами
      const entries = Object.entries(data)
        .filter(([_, value]) => value && typeof value === 'string')
        .map(([_, value]) => value as string)
      return entries.length > 0 ? entries.join('\n\n') : null
    }
    return null
  }
  
  const historyText = formatText(history)
  const benefitsText = formatText(benefits)
  const moodText = formatText(mood)
  
  return (
    <div className="px-4 py-6 space-y-6">
      {/* История */}
      {historyText && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <History className="h-5 w-5 text-primary dark:text-primary-light" />
            <h2 className="text-xl font-semibold text-ink dark:text-white">История</h2>
          </div>
          <div className="p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
              {historyText}
            </p>
          </div>
        </div>
      )}
      
      {/* Полезные свойства */}
      {benefitsText && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Heart className="h-5 w-5 text-primary dark:text-primary-light" />
            <h2 className="text-xl font-semibold text-ink dark:text-white">Полезные свойства</h2>
          </div>
          <div className="p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
              {benefitsText}
            </p>
          </div>
        </div>
      )}

      {/* Состояние */}
      {moodText && (
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-primary dark:text-primary-light" />
            <h2 className="text-xl font-semibold text-ink dark:text-white">Состояние</h2>
          </div>
          <div className="p-4 bg-white dark:bg-dark-card rounded-xl border border-silk dark:border-dark-border">
            <p className="text-base text-gray-700 dark:text-gray-300 leading-relaxed whitespace-pre-line">
              {moodText}
            </p>
          </div>
        </div>
      )}
    </div>
  )
}










